/** @format */

import React from "react";
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { Blog } from "../types";
import { formatDate } from "../utils/formatUtils";
import { useShare } from "../hooks/useShare";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

interface BlogDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  blog: Blog | null;
}

const BlogDetailsModal: React.FC<BlogDetailsModalProps> = ({
  isOpen,
  onClose,
  blog,
}) => {
  const { currentUser } = useAuth();
  const { copyToClipboard } = useShare();
  
  if (!isOpen || !blog) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold">Blog Post</h3>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Blog Content */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Featured Image */}
              <div className="space-y-6">
                {/* Featured Image */}
                {blog.link && (
                  <div className="aspect-video bg-gray-200 rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={blog.link}
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2 inline" />
                    Share This Post
                  </button>
                </div>
              </div>

              {/* Right Column - Blog Information */}
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3 leading-tight">
                    {blog.title}
                  </h1>
                  <p className="text-lg text-gray-600">
                    by{" "}
                    <span className="font-semibold text-blue-600">
                      {blog.author.includes("@")
                        ? blog.author.split("@")[0]
                        : blog.author}
                    </span>
                  </p>
                </div>

                {/* Meta Information */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{formatDate(blog.createdAt)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-blue-600 font-medium">
                      <ClockIcon className="h-4 w-4" />
                      <span>{blog.readingTime} min read</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                      {blog.wordCount} words
                    </span>
                  </div>
                </div>

                {/* Categories */}
                {blog.categories && blog.categories.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Categories
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {blog.categories.map((category, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full shadow-sm"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {blog.tags && blog.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {blog.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {blog.description && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      Summary
                    </h4>
                    <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-xl">
                      {blog.description}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h4 className="text-2xl font-bold text-gray-900 mb-6">Content</h4>
              <div
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed bg-gray-50 p-6 rounded-xl"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </div>

            {/* SEO Information */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                SEO Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {blog.metaDescription && (
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <span className="block text-sm font-semibold text-blue-800 mb-2">
                      Meta Description
                    </span>
                    <p className="text-blue-700 text-sm">
                      {blog.metaDescription}
                    </p>
                  </div>
                )}
                {blog.metaKeywords && (
                  <div className="bg-green-50 p-4 rounded-xl">
                    <span className="block text-sm font-semibold text-green-800 mb-2">
                      Meta Keywords
                    </span>
                    <p className="text-green-700 text-sm">
                      {blog.metaKeywords}
                    </p>
                  </div>
                )}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <span className="block text-sm font-semibold text-gray-800 mb-2">
                    Slug
                  </span>
                  <p className="text-gray-700 text-sm font-mono">{blog.slug}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl">
                  <span className="block text-sm font-semibold text-purple-800 mb-2">
                    Content Type
                  </span>
                  <p className="text-purple-700 text-sm capitalize">
                    {blog.contentType}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-8 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-8 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-300 font-semibold"
              >
                Close
              </button>

              <button
                onClick={() => copyToClipboard(
                  `${window.location.origin}/blog/${blog.slug}`,
                  "Blog URL copied to clipboard!"
                )}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center space-x-2"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailsModal;
